import { supabase } from '../lib/supabase';
import { Contact } from '../types/Contact';
import { listContactsCore, updateContactCore, getContactCore, getContactCountsForUserCore } from '@gv/core';
import { parseNotesToStructuredFields, shouldParseContact } from '../utils/contactDataParser';

export interface MigrationStats {
    total: number;
    processed: number;
    updated: number;
    errors: number;
    skipped: number;
}

/**
 * Parse existing contacts' notes into structured fields
 * This function processes all contacts in batches to avoid overwhelming the database
 */
export async function migrateContactNotesToStructuredFields(
    batchSize: number = 50,
    onProgress?: (stats: MigrationStats) => void
): Promise<MigrationStats> {
    const stats: MigrationStats = {
        total: 0,
        processed: 0,
        updated: 0,
        errors: 0,
        skipped: 0
    };

    try {
        // First, get the total count via core helper
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');
        try {
            const counts = await getContactCountsForUserCore(supabase as any, user.id);
            stats.total = counts.total || 0;
        } catch {
            // If count fails, we'll still proceed without a total
            stats.total = 0;
        }

        console.log(`📊 Starting migration of ${stats.total} contacts...`);

        let offset = 0;
        let hasMore = true;

        while (hasMore) {
            // Fetch a batch of contacts via core
            const contacts = await listContactsCore(supabase as any, { limit: batchSize, offset, includeArchived: true });

            if (!contacts || (contacts as any[]).length === 0) {
                hasMore = false;
                break;
            }

            // Process each contact in the batch
            for (const contact of contacts) {
                try {
                    stats.processed++;

                    // Check if this contact needs parsing
                    if (!shouldParseContact(contact)) {
                        stats.skipped++;
                        continue;
                    }

                    // Parse the notes into structured fields
                    const structuredData = parseNotesToStructuredFields((contact as any).notes);

                    // Only update if we actually extracted meaningful data
                    const hasStructuredData = Object.values(structuredData).some(value =>
                        value && value !== structuredData.parsed_at && String(value).trim().length > 0
                    );

                    if (hasStructuredData) {
                        // Update the contact with structured data
                        try {
                            await updateContactCore(supabase as any, (contact as any).id, structuredData as any);
                            stats.updated++;
                            console.log(`✅ Updated contact: ${(contact as any).first_name} ${(contact as any).last_name} (${(contact as any).id})`);
                        } catch (updateError) {
                            console.error(`Error updating contact ${(contact as any).id}:`, updateError);
                            stats.errors++;
                        }
                    } else {
                        // Still update parsed_at to mark as processed
                        try {
                            await updateContactCore(supabase as any, (contact as any).id, { parsed_at: (structuredData as any).parsed_at } as any);
                            stats.skipped++;
                        } catch (updateError) {
                            console.error(`Error updating parsed_at for contact ${(contact as any).id}:`, updateError);
                            stats.errors++;
                        }
                    }

                    // Call progress callback if provided
                    if (onProgress) {
                        onProgress(stats);
                    }

                } catch (error) {
                    console.error(`Error processing contact ${contact.id}:`, error);
                    stats.errors++;
                }

                // Small delay to avoid rate limiting
                await new Promise(resolve => setTimeout(resolve, 10));
            }

            offset += batchSize;
            console.log(`📊 Processed batch: ${offset} of ${stats.total} contacts`);

            // Small delay between batches
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        console.log('📊 Migration completed:', stats);
        return stats;

    } catch (error) {
        console.error('Migration failed:', error);
        throw error;
    }
}

/**
 * Parse a single contact's notes into structured fields
 */
export async function parseContactNotes(contactId: string): Promise<boolean> {
    try {
        // Fetch the contact via core
        const contact = await getContactCore(supabase as any, contactId);
        if (!contact) throw new Error('Contact not found');

        // Check if parsing is needed
        if (!shouldParseContact(contact)) {
            console.log(`Contact ${contactId} doesn't need parsing`);
            return false;
        }

        // Parse the notes
        const structuredData = parseNotesToStructuredFields((contact as any).notes);

        // Update the contact
        await updateContactCore(supabase as any, contactId, structuredData as any);

        console.log(`✅ Successfully parsed contact ${contactId}`);
        return true;

    } catch (error) {
        console.error(`Error parsing contact ${contactId}:`, error);
        throw error;
    }
}
